// Home — landing page that lists saved CourseDrafts + offers "+ New Course".
//
// Decision history:
//   §4 (19E): basic listing, reads drafts from IDB on mount.
//   §O3 (19O): visual hero with display serif title, brand seal art,
//     amber CTA, refined card layout with countdown chip.
//   19BI+ refactor: Quick-create removed — /create is the single entry point.
//     Home page is now purely a draft list + hero CTA.

import {  useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Calendar, ArrowUpRight, Settings, Trash2, HelpCircle } from 'lucide-react';
import type { CourseDraft } from '@examforge/shared/schemas';
import { listDrafts } from '../storage/idb.js';
import { useT, useLocale } from '../i18n/index.js';
import { COMPONENT_MANIFEST } from '@examforge/shared';
import { deleteEntireCourse } from '../lib/delete-course.js';
import { daysUntil } from '../lib/days-until.js';
import { OnboardingOverlay, shouldShowOnboard } from './onboarding-overlay.js';
import { useToast } from '../components/toast.js';
import { StateCard } from '../components/state-card.js';
import { useNotify } from '../lib/hooks/use-notify.js';

export function HomePage() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const toast = useToast();
  const notify = useNotify();
  const [drafts, setDrafts] = useState<CourseDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 19BI · onboarding overlay: show when no drafts + not dismissed
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    listDrafts()
      .then((d) => {
        setDrafts(d.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
        if (d.length === 0 && shouldShowOnboard()) setShowOnboard(true);
      })
      .catch((e) => setError((e as Error).message));
  }, []);

  // 19AR · G1: per course-card actions live on the home page (per陛下 correction
  // — the dashboard ChapterCard is per-chapter, not per-course).
  const goToSettings = (id: string) => navigate(`/course/${id}/settings`);
  function deleteCourse(d: CourseDraft): void {
    // 19BM-C: course deletion is destructive + irreversible. Migrated from a
    // double window.confirm to a single sticky toast with an action button.
    // The double-confirm was belt-and-suspenders; a single explicit "确认删除"
    // action on a sticky toast is equivalent safety with less friction.
    toast.warn(t('courseSettings.deleteCourseConfirm2'), {
      sticky: true,
      action: {
        label: t('courseSettings.deleteCourse'),
        onClick: () => {
          void deleteEntireCourse(d).then(() => {
            setDrafts((prev) => prev ? prev.filter((x) => x.id !== d.id) : prev);
            // 19BN Phase D D6: notify the message center (toast is the immediate
            // feedback; the notification is the persistent record).
            notify({
              source: 'system',
              level: 'info',
              title: '课程已删除',
              message: d.title,
              persistent: false,
            });
          });
        },
      },
    });
  }

  return (
    <main className="home-main">
      {/* 19BI · onboarding overlay for first-time users */}
      {showOnboard && <OnboardingOverlay onDismiss={() => setShowOnboard(false)} />}
      <section className="home-hero">
        <div className="home-hero-art" aria-hidden="true">
          <HeroSeal />
        </div>
        <div className="home-hero-text">
          <p className="home-hero-eyebrow">{t('app.tagline')}</p>
          <h1 className="home-title">{t('home.title')}</h1>
          <p className="home-subtitle">{t('home.subtitle')}</p>
          <div className="home-actions">
            <Link to="/create" className="btn-primary btn-primary-lg">
              <Plus size={16} aria-hidden="true" />
              {t('form.createCourse')}
            </Link>
            <Link to="/guide" className="home-guide-link">
              <HelpCircle size={14} aria-hidden="true" />
              {t('guide.linkText')}
            </Link>
          </div>

          {/* Session 19AR · G5: DEV-only operator dashboard entry.
           *  19BB-PROD: prod admin (cross-user telemetry from D1) lives at
           *  /admin?key=…; this DEV-only link points at the local IDB-only
           *  fallback (/__operator). */}
          {import.meta.env.DEV && (
            <Link to="/__operator" className="home-operator-link" title={t('home.operatorLinkTitle')}>
              {t('home.operatorLinkText')}
            </Link>
          )}
        </div>
      </section>

      {error && <p className="form-error">{error}</p>}

      {drafts && drafts.length === 0 && (
        <StateCard
          variant="empty"
          icon={<SmallSeal />}
          title={t('home.empty')}
          hint={t('home.emptyHint')}
        />
      )}

      {drafts && drafts.length > 0 && (
        <ul className="home-draft-list">
          {drafts.map((d) => {
            // E-2 (19BM): daysUntil util handles null + NaN internally —
            // invalid examDate no longer leaks NaN into the countdown UI.
            const examDays = daysUntil(d.examDate);
            return (
              <li key={d.id} className="home-draft-li">
                {/* 19AR · G1: action buttons live OUTSIDE the <Link> so clicks
                    don't bubble + navigate. They're absolutely positioned in
                    the top-right corner of the card. */}
                <div className="home-draft-actions">
                  <button
                    type="button"
                    className="home-draft-action-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToSettings(d.id); }}
                    aria-label={t('courseSettings.heading')}
                    title={t('courseSettings.heading')}
                  >
                    <Settings size={13} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="home-draft-action-btn home-draft-action-btn-danger"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); void deleteCourse(d); }}
                    aria-label={t('courseSettings.deleteCourse')}
                    title={t('courseSettings.deleteCourse')}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                </div>
                <Link to={`/course/${d.id}`} className="home-draft-card">
                  <header className="home-draft-card-head">
                    <span className="home-draft-title">{d.title}</span>
                    {examDays !== null && (
                      <span
                        className={`home-draft-countdown${examDays <= 7 ? ' is-urgent' : ''}`}
                        title={d.examDate ?? ''}
                      >
                        <Calendar size={11} aria-hidden="true" />
                        {examDays > 0
                          ? t('home.countdownDays', { n: examDays })
                          : examDays === 0
                            ? t('home.countdownToday')
                            : t('home.countdownPast')}
                      </span>
                    )}
                  </header>
                  <span className="home-draft-meta">
                    {disciplineLabel(d, locale, t)} · {d.pedagogyTypes.map((p) => pedagogyLabel(p, locale, t)).join(' / ')}
                  </span>
                  <span className="home-draft-components">
                    {d.enabledComponents
                      .filter((id) => id !== 'dashboard' && id !== 'wrong_answers')
                      .map((id) => {
                        const m = COMPONENT_MANIFEST.find((e) => e.id === id);
                        return m ? m.label[locale] : id;
                      })
                      .join(' · ')}
                  </span>
                  <span className="home-draft-cta">
                    {t('home.openCourse')}
                    <ArrowUpRight size={12} aria-hidden="true" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

/** Large hero seal — bigger version of the brand mark, stylized for landing impact. */
function HeroSeal() {
  const t = useT();
  return (
    <svg viewBox="0 0 120 120" width="120" height="120" role="img" aria-label={t('home.sealAlt')}>
      <defs>
        <radialGradient id="heroSealBg" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="#2a5080" />
          <stop offset="100%" stopColor="#1e3a5f" />
        </radialGradient>
      </defs>
      <circle cx="60" cy="60" r="56" fill="url(#heroSealBg)" />
      <circle cx="60" cy="60" r="52" fill="none" stroke="#fef2ee" strokeWidth="0.6" opacity="0.4" />
      <circle cx="60" cy="60" r="48" fill="none" stroke="#c2410c" strokeWidth="1.2" opacity="0.85" />
      <text
        x="60"
        y="78"
        textAnchor="middle"
        fontSize="64"
        fontFamily="'Fraunces Variable', 'Fraunces', 'Songti SC', 'STSong', serif"
        fontWeight="600"
        fill="#fef2ee"
        style={{ fontVariationSettings: "'opsz' 144" }}
      >
        考
      </text>
      {/* small cinnabar accent dot — like a 印章 highlight */}
      <circle cx="92" cy="32" r="3" fill="#c2410c" opacity="0.85" />
    </svg>
  );
}

/** Smaller seal for empty-state. */
function SmallSeal() {
  const t = useT();
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" role="img" aria-label={t('home.sealAlt')}>
      <circle cx="32" cy="32" r="30" fill="#1e3a5f" opacity="0.08" />
      <circle cx="32" cy="32" r="28" fill="none" stroke="#1e3a5f" strokeWidth="0.5" opacity="0.4" />
      <text
        x="32"
        y="42"
        textAnchor="middle"
        fontSize="32"
        fontFamily="'Fraunces Variable', 'Songti SC', serif"
        fontWeight="600"
        fill="#1e3a5f"
        opacity="0.4"
      >
        考
      </text>
    </svg>
  );
}

// Helpers — translate enum values to localized strings via i18n.
function disciplineLabel(d: CourseDraft, locale: 'zh' | 'en', t: (key: never) => string): string {
  if (d.discipline === 'other' && d.customDiscipline) return d.customDiscipline;
  const key = `form.step1.discipline${disciplineSuffix(d.discipline)}` as never;
  return t(key);
}

function pedagogyLabel(p: string, _locale: 'zh' | 'en', t: (key: never) => string): string {
  const suffix = p === 'case_study' ? 'Case'
    : p === 'conceptual' ? 'Conceptual'
    : p === 'quantitative' ? 'Quantitative'
    : p === 'lab_practice' ? 'Lab'
    : p === 'memorization' ? 'Memorization'
    : p === 'essay_writing' ? 'Essay'
    : p === 'coding' ? 'Coding'
    : 'Custom';
  return t(`form.step1.pedagogy${suffix}` as never);
}

function disciplineSuffix(d: string): string {
  return ({
    humanities: 'Humanities',
    languages: 'Languages',
    arts: 'Arts',
    business: 'Business',
    economics: 'Economics',
    law: 'Law',
    social_sciences: 'Social',
    mathematics: 'Mathematics',
    physics: 'Physics',
    chemistry: 'Chemistry',
    biology_medicine: 'Biology',
    computer_science: 'CS',
    engineering: 'Engineering',
    other: 'Other',
  } as Record<string, string>)[d] ?? 'Other';
}
